import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Card,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Tooltip,
    Alert,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Divider,
} from "@mui/material";
import { Add, Edit, Delete, ArrowUpward, ArrowDownward, Lock, VerifiedUser } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import statusDefinitionService from "../../services/statusDefinitionService";
import { fetchStatusDefinitions } from "../../redux/slices/statusDefinitionsSlice";
import PageHeader from "../../components/common/PageHeader";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import useUrlState from "../../hooks/useUrlState";

const DOMAINS = [
    { key: "shipment", label: "Shipments" },
    { key: "item", label: "Shipment Items" },
    { key: "deliveryReceipt", label: "Delivery Receipts" },
    { key: "unclaimedPackage", label: "Unclaimed Packages" },
    { key: "invoice", label: "Invoices" },
    { key: "payment", label: "Payments" },
    { key: "sailing", label: "Sailing Schedules" },
    { key: "vehicle", label: "Vehicle Shipments" },
];

const emptyForm = { label: "", color: "#4A5568", bg: "#EDF2F7", roles: [] };

// Module-level (not inline in the component) so useUrlState gets a stable
// reference every render — see that hook's own doc comment for why.
// `domain` is already a stable key (e.g. "shipment"), so it's used
// straight in the URL with no slugifying needed.
const URL_SCHEMA = {
    domain: { default: DOMAINS[0].key },
};

const AdminStatusSettings = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [urlState, setUrlState] = useUrlState(URL_SCHEMA);
    const domain = DOMAINS.some((d) => d.key === urlState.domain) ? urlState.domain : DOMAINS[0].key;
    const setDomain = (d) => setUrlState({ domain: d });
    const [statuses, setStatuses] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingStatus, setEditingStatus] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const load = async (d = domain) => {
        setLoading(true);
        try {
            const { data } = await statusDefinitionService.getByDomain(d);
            setStatuses(data.data.statuses || []);
            setAvailableRoles(data.data.availableRoles || []);
        } catch {
            enqueueSnackbar("Failed to load statuses", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(domain);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain]);

    const refreshEverywhere = () => {
        load();
        dispatch(fetchStatusDefinitions());
    };

    const move = async (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= statuses.length) return;
        const reordered = [...statuses];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        setStatuses(reordered);
        try {
            await statusDefinitionService.reorder(domain, reordered.map((s) => s._id));
            refreshEverywhere();
        } catch {
            enqueueSnackbar("Failed to reorder statuses", { variant: "error" });
            load();
        }
    };

    const openCreate = () => {
        setEditingStatus(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const openEdit = (status) => {
        setEditingStatus(status);
        setForm({ label: status.label, color: status.color, bg: status.bg, key: status.key, roles: status.roles || [] });
        setDialogOpen(true);
    };

    const toggleRole = (role) => {
        setForm((f) => ({
            ...f,
            roles: f.roles.includes(role) ? f.roles.filter((r) => r !== role) : [...f.roles, role],
        }));
    };

    // Which status (if any) in the currently-loaded list already holds a role
    // — shown so admins know reassigning it will take it away from that one.
    const holderOf = (role) => statuses.find((s) => s._id !== editingStatus?._id && (s.roles || []).includes(role));

    const handleSave = async () => {
        if (!form.label.trim()) {
            enqueueSnackbar("Label is required", { variant: "error" });
            return;
        }
        setSaving(true);
        try {
            if (editingStatus) {
                await statusDefinitionService.update(domain, editingStatus._id, {
                    label: form.label,
                    color: form.color,
                    bg: form.bg,
                    roles: form.roles,
                });
                enqueueSnackbar("Status updated", { variant: "success" });
            } else {
                await statusDefinitionService.create(domain, {
                    key: form.key || form.label,
                    label: form.label,
                    color: form.color,
                    bg: form.bg,
                });
                enqueueSnackbar("Status created", { variant: "success" });
            }
            setDialogOpen(false);
            refreshEverywhere();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to save status", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (status) => {
        if (!window.confirm(`Delete status "${status.label}"?`)) return;
        try {
            await statusDefinitionService.remove(domain, status._id);
            enqueueSnackbar("Status deleted", { variant: "success" });
            refreshEverywhere();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to delete status", { variant: "error" });
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Status Settings"
                subtitle="Add, relabel, recolor, and reorder the statuses used across the platform"
            />

            <Alert severity="info" sx={{ borderRadius: 2 }}>
                System statuses (locked icon) can't be renamed or deleted, but you can freely change their label,
                color, and order — and add custom statuses anywhere in the flow. Automated behaviors (sends to the
                rider pool, marks an invoice paid, etc.) are assigned per-status from the edit dialog, so you can
                move one onto a different — even custom — status at any time.
            </Alert>

            <Card>
                <Tabs
                    value={domain}
                    onChange={(_, val) => setDomain(val)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: "1px solid", borderColor: "divider", px: 2 }}
                >
                    {DOMAINS.map((d) => (
                        <Tab key={d.key} value={d.key} label={d.label} />
                    ))}
                </Tabs>

                <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
                    <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
                        Add Status
                    </Button>
                </Box>

                {loading ? (
                    <SkeletonLoader rows={5} />
                ) : (
                    <List sx={{ px: 2, pb: 2 }}>
                        {statuses.map((status, index) => (
                            <ListItem
                                key={status._id}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    mb: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                                secondaryAction={
                                    <Box sx={{ display: "flex", gap: 0.5 }}>
                                        <IconButton size="small" disabled={index === 0} onClick={() => move(index, -1)}>
                                            <ArrowUpward fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={index === statuses.length - 1}
                                            onClick={() => move(index, 1)}
                                        >
                                            <ArrowDownward fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => openEdit(status)}>
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <Tooltip title={status.isSystem ? "System statuses can't be deleted" : "Delete"}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={status.isSystem}
                                                    onClick={() => handleDelete(status)}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                }
                            >
                                <Chip
                                    label={status.label}
                                    size="small"
                                    sx={{ bgcolor: status.bg, color: status.color, fontWeight: 600, mr: 1 }}
                                />
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                                            <Typography variant="body2" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                                                {status.key}
                                            </Typography>
                                            {status.isSystem && <Lock sx={{ fontSize: 14, color: "text.disabled" }} />}
                                            {(status.roles || []).map((r) => {
                                                const meta = availableRoles.find((ar) => ar.role === r);
                                                return (
                                                    <Tooltip key={r} title={meta?.description || ""}>
                                                        <Chip
                                                            icon={<VerifiedUser sx={{ fontSize: 12 }} />}
                                                            label={meta?.label || r}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ height: 20, fontSize: 11 }}
                                                        />
                                                    </Tooltip>
                                                );
                                            })}
                                        </Box>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Card>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{editingStatus ? "Edit Status" : "Add Status"}</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                    {!editingStatus && (
                        <TextField
                            label="Key (optional, auto-generated from label)"
                            fullWidth
                            value={form.key || ""}
                            onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
                            helperText="Lowercase, underscore-separated identifier"
                        />
                    )}
                    <TextField
                        label="Label *"
                        fullWidth
                        value={form.label}
                        onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                    />
                    <TextField
                        label="Text Color"
                        type="color"
                        value={form.color}
                        onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                    />
                    <TextField
                        label="Background Color"
                        type="color"
                        value={form.bg}
                        onChange={(e) => setForm((f) => ({ ...f, bg: e.target.value }))}
                    />
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Preview
                        </Typography>
                        <Chip label={form.label || "Label"} sx={{ bgcolor: form.bg, color: form.color, fontWeight: 600 }} />
                    </Box>

                    {editingStatus && availableRoles.length > 0 && (
                        <>
                            <Divider />
                            <Box>
                                <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>
                                    Automated Behavior
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                    Assign which automated behaviors this status drives. Each behavior can only be
                                    held by one status at a time — checking one here takes it away from wherever it
                                    currently lives.
                                </Typography>
                                <FormGroup>
                                    {availableRoles.map((r) => {
                                        const holder = holderOf(r.role);
                                        return (
                                            <FormControlLabel
                                                key={r.role}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={form.roles.includes(r.role)}
                                                        onChange={() => toggleRole(r.role)}
                                                    />
                                                }
                                                label={
                                                    <Box>
                                                        <Typography variant="body2">{r.label}</Typography>
                                                        {r.description && (
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                {r.description}
                                                            </Typography>
                                                        )}
                                                        {holder && !form.roles.includes(r.role) && (
                                                            <Typography variant="caption" sx={{ color: "warning.main" }} display="block">
                                                                Currently held by "{holder.label}"
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                                sx={{ alignItems: "flex-start", mb: 1 }}
                                            />
                                        );
                                    })}
                                </FormGroup>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {editingStatus ? "Save Changes" : "Create Status"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminStatusSettings;
